import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

import { prefetchPageDataCache } from '../../api/hooks/usePageData';
import { useRouter } from '../../application/Router';
import { formatUrl, isLocalUrl, normalizeUrl } from '../../utils/url-helpers';
import LinkViewModel from '../../view-models/LinkViewModel';
import { trackEvent } from '../../utils/tracking';

export type AnchorProps = LinkViewModel & {
  replaceHistory?: boolean;
  prefetch?: boolean;
  /** This Anchor is always going to be an External page */
  external?: boolean;
} & React.PropsWithoutRef<JSX.IntrinsicElements['a']>;

function isModifiedEvent(event) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

const Anchor = forwardRef(
  (
    {
      href,
      children,
      label,
      title,
      target,
      onClick,
      replaceHistory,
      prefetch,
      external,
      ...rest
    }: AnchorProps,
    ref: any,
  ) => {
    const {
      replace,
      push,
      pathname,
      search,
      location: currentLocation,
    } = useRouter();
    const anchorRef = useRef<HTMLAnchorElement | null>(null);

    const hasHref = typeof href === 'string' && href.length > 0;
    const normalized = hasHref ? normalizeUrl(href) : undefined;
    const isLocal =
      hasHref && !external && normalized
        ? isLocalUrl(normalized, target)
        : false;

    if (isLocal && normalized?.pathname) {
      // If the URL is relative to the current site, sanitize the href
      href = formatUrl(normalized);
    }

    const setRef = useCallback(
      (node) => {
        anchorRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const normalizedSearch = normalized?.search || '';
    const normalizedHash = normalized?.hash || '';
    const currentHash = currentLocation.hash || '';
    const isCurrentLocation =
      isLocal &&
      normalized?.pathname &&
      pathname === normalized.pathname &&
      search === normalizedSearch &&
      currentHash === normalizedHash;
    const currentPage = isCurrentLocation ? 'page' : undefined;
    const prefetchPathname =
      prefetch && isLocal && normalized?.pathname
        ? normalized.pathname
        : undefined;

    useEffect(() => {
      const anchorElement = anchorRef.current;
      const canObserve =
        prefetchPathname &&
        anchorElement &&
        typeof IntersectionObserver !== 'undefined';

      if (!canObserve) {
        return undefined;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;

          // Prefetch the next page once the link enters the viewport.
          observer.disconnect();
          prefetchPageDataCache(prefetchPathname);
        },
        { threshold: 0.5 },
      );

      observer.observe(anchorElement);

      return () => observer.disconnect();
    }, [prefetchPathname]);

    const handleClick = (event) => {
      if (onClick) {
        onClick(event);
      }

      if (href && !isLocal) {
        trackEvent('Outbound Link: Click', { props: { url: href } });
      }

      if (
        href &&
        isLocal &&
        !event.defaultPrevented && // onClick prevented default
        event.button === 0 && // ignore everything but left clicks
        target !== '_blank' && // let browser handle "target=_blank" etc.
        !isModifiedEvent(event) // ignore clicks with modifier keys
      ) {
        // Block the normal anchor event
        event.preventDefault();

        if (isCurrentLocation) {
          return;
        }

        // Create a To object with the URL values. This ensures the correct values are used when we update the router location.
        const to = {
          hash: normalized?.hash || undefined,
          pathname: normalized?.pathname || undefined,
          search: normalized?.search || undefined,
        };

        if (replaceHistory && replace) {
          replace(to);
        } else {
          push(to);
        }
      }
    };

    return (
      <a
        aria-current={currentPage}
        ref={setRef}
        href={href}
        title={title !== label ? title : undefined}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        target={target}
        sx={{
          fontSize: '1em',
          color: 'currentColor',
          overflowWrap: 'break-word',
        }}
        onClick={handleClick}
        {...rest}
      >
        {children || label}
      </a>
    );
  },
);

Anchor.displayName = 'Anchor';

export default Anchor;
